import { Code } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { FC, ReactNode } from 'react';
import { BaseLineCheck2 } from '@/app/components/icons/BaseLineCheck2';
import { BaseLineConvention } from '@/app/components/icons/BaseLineConvention';
import { BaseLineFeedBack2 } from '@/app/components/icons/BaseLineFeedBack2';
import { BaseLineHelp } from '@/app/components/icons/BaseLineHelp';
import { BaseLineLanguage } from '@/app/components/icons/BaseLineLanguage';
import { BaseLineProfile } from '@/app/components/icons/BaseLineProfile';
import { BaseLineSub } from '@/app/components/icons/BaseLineSub';
import { BaseLineSwap } from '@/app/components/icons/BaseLineSwap';
import { ChannelDiscord3 } from '@/app/components/icons/ChannelDiscord3';
import { ChannelWechat3 } from '@/app/components/icons/ChannelWechat3';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import {
  EN_FAQ_URL,
  GLOBAL_LANGUAGES_CONFIG,
  isEnterpriseDistribution,
  QRCODE_URL,
  SITE,
  ZH_FAQ_URL,
} from '@/app/constants';
import { useTeamSpaces } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { SupportedLanguage } from '@/app/types';
import {
  cn,
  getFeedBackUrl,
  getSubscriptionUrl,
  PERSONAL_TEAM_SPACE_ID,
  reportEvent,
  setRegisterPage,
} from '@/app/utils';
import { openOrRedirect, safeStorage } from '@/app/utils/client';
import { useLocale, usePathname, useRouter } from '@/i18n/routing';
import { UserAvatar } from '../UserAvatar';
import { SUBS_PLAN_FREE } from './constants';
import { GetTokenDialog, useViduToken } from './GetTokenDialog';
import { useSignOut } from './use-sign-out';

interface MenuItem {
  key: string;
  label?: ReactNode;
  type: 'item' | 'danger' | 'divider';
  icon?: ReactNode;
  subItems?: SubMenuItem[];
  onClick?: () => void;
  onMouseEnter?: () => void;
}

interface SubMenuItem {
  key: string;
  label: ReactNode;
  href?: string;
  selected?: boolean;
  onClick?: () => void;
}

type PCUserMenuProps = {
  popoverOpen: boolean;
  onPopoverOpenChange: (open: boolean) => void;
  /** 触发器外层容器样式，用于让头像点击区与侧边栏其它按钮保持一致 */
  triggerClassName?: string;
  /** 头像尺寸等样式覆盖，默认 size-9(36px) */
  avatarClassName?: string;
};

export const PCUserMenu: FC<PCUserMenuProps> = ({
  popoverOpen,
  onPopoverOpenChange,
  triggerClassName,
  avatarClassName,
}) => {
  const { userInfo, isTeamSpace } = useStore((state) => ({
    userInfo: state.userInfo,
    isTeamSpace: state.isTeamSpace,
  }));
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();

  const { teamSpaces, currentSpaceId, switchToSpace } = useTeamSpaces();

  const signOut = useSignOut();
  const {
    open: openTokenDialog,
    setOpen: setOpenTokenDialog,
    token: viduToken,
    requestToken: requestViduToken,
    handleReset: handleResetViduToken,
  } = useViduToken();

  const handleSubMenuItemClick = (item: SubMenuItem) => {
    if (item.href) {
      openOrRedirect(item.href);
    } else {
      item.onClick?.();
    }
    onPopoverOpenChange(false);
  };

  const handleLanguageChange = (language: SupportedLanguage) => {
    router.replace(pathname, { locale: language });
    setRegisterPage({
      language,
    });
  };

  // 判断社交媒体显示逻辑
  const socialMenuItem: MenuItem | null =
    SITE === 'china'
      ? {
          // 中国站显示微信
          key: 'wechat',
          label: '加入微信讨论群',
          type: 'item' as const,
          icon: (
            <div>
              <ChannelWechat3 className="cursor-pointer text-xl" />
              <div className="absolute top-1/2 left-[calc(100%+12px)] z-50 hidden -translate-y-1/2 group-hover:block">
                <div className="h-[248px] w-[216px] rounded-12 border border-system-white12 bg-system-bg02 px-2 py-3">
                  <div className="mx-auto h-[200px] w-[200px]">
                    <ImageWithPlaceholder
                      src={QRCODE_URL}
                      alt="微信群二维码"
                      className="h-full w-full rounded-6 bg-[#3C4352]"
                    />
                  </div>
                  <p className="mt-2 text-center font-[400] text-system-white64 text-xs tracking-[0]">
                    加入讨论群，了解最新动态
                  </p>
                </div>
              </div>
            </div>
          ),
        }
      : locale === 'zh'
        ? null // 海外站且语言为中文时不显示任何社交媒体选项
        : {
            // 海外站且语言为非中文时显示 Discord
            key: 'discord',
            label: t('helpCenter.connectOnDiscord'),
            type: 'item' as const,
            icon: <ChannelDiscord3 className="flex-shrink-0 text-xl" />,
            onClick: () => {
              openOrRedirect('https://discord.gg/Ymeg4kDbmF');
            },
          };

  const chinaAgreementMenuItems: SubMenuItem[] = [
    {
      key: 'community',
      label: '平台自律公约',
      href: '/terms/community',
    },
    {
      key: 'privacy',
      label: '隐私政策',
      href: '/privacy',
    },
    {
      key: 'privacy-summary',
      label: t('home3.footer.privacySummary'),
      href: '/privacy-summary',
    },
    {
      key: 'personal-info-collection',
      label: t('home3.footer.personalInfoCollection'),
      href: '/personal-info-collection',
    },
    {
      key: 'personal-info-sharing',
      label: t('home3.footer.personalInfoSharing'),
      href: '/personal-info-sharing',
    },
    {
      key: 'terms',
      label: '用户协议',
      href: '/terms',
    },
  ];

  const languageMenuItems: SubMenuItem[] = GLOBAL_LANGUAGES_CONFIG.map(({ value, label }) => ({
    key: `language-${value}`,
    label,
    selected: locale === value,
    onClick: () => handleLanguageChange(value as SupportedLanguage),
  }));

  const getWorkspaceName = (spaceId?: string, teamName?: string) =>
    spaceId === PERSONAL_TEAM_SPACE_ID
      ? t('teamProfile.personalWorkspace')
      : t('teamProfile.workspaceWithName', { name: teamName || '' });

  const workspaceMenuItems: SubMenuItem[] = teamSpaces
    .filter((space) => Boolean(space.space_id))
    .map((space) => ({
      key: `workspace-${space.space_id}`,
      label: <span className="min-w-0 flex-1 truncate">{getWorkspaceName(space.space_id, space.team_name)}</span>,
      selected: space.space_id === currentSpaceId,
      onClick: () => switchToSpace(space.space_id),
    }));

  const userCenterMenu: MenuItem[] = (
    isEnterpriseDistribution
      ? []
      : [
          ...(workspaceMenuItems.length > 0
            ? [
                {
                  key: 'workspace',
                  type: 'item' as const,
                  label: t('teamProfile.switchTeam'),
                  icon: <BaseLineSwap className="flex-shrink-0 text-xl" />,
                  subItems: workspaceMenuItems,
                },
              ]
            : []),
          ...(!isTeamSpace
            ? [
                {
                  key: 'profile',
                  label: t('sidebar.profile'),
                  type: 'item' as const,
                  icon: <BaseLineProfile className="flex-shrink-0 text-xl" />,
                  onClick: () => {
                    if (!userInfo?.id) return;
                    router.push(`/profile/${userInfo.id}`);
                    onPopoverOpenChange(false);
                  },
                },
              ]
            : []),
          {
            key: 'subscribe',
            label: t('personal.subscribe'),
            type: 'item' as const,
            icon: <BaseLineSub className="flex-shrink-0 text-xl" />,
            onClick: () => {
              router.push(getSubscriptionUrl(isTeamSpace));
              onPopoverOpenChange(false);
              reportEvent('page_view', {
                page_name: 'my-subscription',
                from_page: 'profile_my_subscription',
              });
            },
          },
          {
            key: 'vidu-token',
            label: 'Vidu Token',
            type: 'item' as const,
            icon: <Code size={20} className="flex-shrink-0" />,
            onClick: () => {
              onPopoverOpenChange(false);
              requestViduToken();
            },
          },
          {
            key: 'help',
            label: t('personal.help'),
            type: 'item' as const,
            icon: <BaseLineHelp className="flex-shrink-0 text-xl" />,
            onClick: () => {
              openOrRedirect(locale === 'zh' ? ZH_FAQ_URL : EN_FAQ_URL);
            },
          },
          ...(SITE === 'china'
            ? [
                {
                  key: 'agreement',
                  label: '平台协议',
                  type: 'item' as const,
                  icon: <BaseLineConvention className="flex-shrink-0 text-xl" />,
                  subItems: chinaAgreementMenuItems,
                },
              ]
            : [
                {
                  key: 'cookies-policy',
                  label: t('home3.footer.cookiesPolicy'),
                  type: 'item' as const,
                  icon: <BaseLineConvention className="flex-shrink-0 text-xl" />,
                  onClick: () => {
                    openOrRedirect('/cookies');
                  },
                },
              ]),
          ...(userInfo?.subs_plan !== SUBS_PLAN_FREE
            ? [
                {
                  key: 'feedback',
                  label: t('personal.feedback'),
                  type: 'item' as const,
                  icon: <BaseLineFeedBack2 className="flex-shrink-0 text-xl" />,
                  onClick: () => {
                    const feedBackUrl = getFeedBackUrl();
                    openOrRedirect(feedBackUrl);
                  },
                },
              ]
            : []),
          ...(SITE === 'global'
            ? [
                {
                  key: 'language',
                  type: 'item' as const,
                  label: t('sidebar.systemItems.language'),
                  icon: <BaseLineLanguage className="flex-shrink-0 text-xl" />,
                  subItems: languageMenuItems,
                },
              ]
            : []),
          ...(socialMenuItem ? [socialMenuItem] : []), // 只在有社交媒体选项时添加到菜单
          { type: 'divider' as const, key: 'divider' },
        ]
  ).concat([
    {
      key: 'logout',
      label: t('personal.logout'),
      type: 'danger' as const,
      onClick: () => {
        safeStorage.removeItem('utm_term');
        signOut();
      },
    },
  ]);

  if (!userInfo) return null;
  return (
    <div>
      {!isEnterpriseDistribution && (
        <GetTokenDialog
          open={openTokenDialog}
          onOpenChange={setOpenTokenDialog}
          token={viduToken}
          onReset={handleResetViduToken}
        />
      )}
      <DropdownMenu open={popoverOpen} onOpenChange={onPopoverOpenChange}>
        <DropdownMenuTrigger asChild>
          <div
            className={cn(
              triggerClassName && 'flex cursor-pointer items-center justify-center transition-colors',
              triggerClassName,
            )}
          >
            <UserAvatar userInfo={userInfo} className={cn('cursor-pointer', avatarClassName)} />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="right"
          sideOffset={8}
          align="end"
          className="w-[240px] select-none overflow-visible rounded-12 border border-system-white12 bg-system-bg02 p-2 [box-shadow:0px_20px_60px_0px_rgba(0,0,0,0.32)]"
        >
          {userCenterMenu.map((item) => {
            if (item.type === 'divider') {
              return <DropdownMenuSeparator key={`user-center-divider-${item.type}`} className="mx-1 my-1" />;
            }
            if (item.type === 'danger') {
              return (
                <DropdownMenuItem
                  key={`user-center-danger-${item.label}`}
                  className="flex cursor-pointer items-center gap-2 rounded-8 px-2 py-3 text-sm text-system-white64 leading-6 hover:bg-[rgba(255,48,48,0.12)] hover:text-white"
                  onClick={item.onClick}
                >
                  {item.icon}
                  {item.label}
                </DropdownMenuItem>
              );
            }

            if (item.subItems) {
              return (
                <DropdownMenuSub key={`user-center-sub-${item.key}`}>
                  <DropdownMenuSubTrigger className="gap-2 rounded-8 px-2 py-3 text-sm text-white leading-6 hover:bg-system-hover01 focus:bg-system-hover01 data-[state=open]:bg-system-hover01">
                    {item.icon}
                    {item.label}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent
                      sideOffset={12}
                      className="w-[220px] rounded-12 border border-system-white12 bg-system-bg02 p-4 [box-shadow:0px_20px_60px_0px_rgba(0,0,0,0.32)]"
                    >
                      {item.subItems.map((subItem) => (
                        <DropdownMenuItem
                          key={`user-center-sub-item-${subItem.key}`}
                          className="flex h-10 items-center justify-between whitespace-nowrap rounded-8 px-2 text-sm text-white leading-6 hover:bg-system-hover01 focus:bg-system-hover01"
                          onClick={() => handleSubMenuItemClick(subItem)}
                        >
                          {subItem.label}
                          {subItem.selected ? <BaseLineCheck2 className="text-lg" /> : null}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              );
            }

            return (
              <DropdownMenuItem
                key={`user-center-item-${item.key}`}
                className="group relative flex cursor-pointer items-center gap-2 rounded-8 px-2 py-3 text-sm text-white leading-6 hover:bg-system-hover01 focus:bg-system-hover01"
                onClick={item.onClick}
              >
                {item.icon}
                {item.label}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
